import { Check, ExternalLink, Heart, MapPin, Sparkles, UtensilsCrossed } from 'lucide-react';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { Modal } from '../components/Modal';
import { SectionHeading } from '../components/SectionHeading';
import { Sticker } from '../components/Sticker';
import { birthdayConfig, type Hotel } from '../config';
import { useLocalStorage } from '../hooks/useLocalStorage';

type Props = {
  selectedHotelId: string;
  onSelectHotel: (id: string) => void;
};

export function HotelsSection({ selectedHotelId, onSelectHotel }: Props) {
  const [filter, setFilter] = useState('All');
  const [details, setDetails] = useState<Hotel | null>(null);
  const [favourites, setFavourites] = useLocalStorage<string[]>('birthday-favourite-hotels', []);

  const visibleHotels = useMemo(
    () => birthdayConfig.hotels.filter((hotel) => filter === 'All' || hotel.filters.includes(filter)),
    [filter],
  );

  const selectHotel = (hotel: Hotel) => {
    onSelectHotel(hotel.id);
    window.setTimeout(() => document.getElementById('dinner-summary')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
  };

  return (
    <section id="hotels" className="section-shell hotels-section">
      <div className="page-container">
        <SectionHeading
          eyebrow={birthdayConfig.copy.hotels.eyebrow}
          title={birthdayConfig.copy.hotels.title}
          description={birthdayConfig.copy.hotels.description}
        />

        <div className="filter-chips" aria-label="Filter Lahore dinner options">
          {birthdayConfig.copy.hotels.filters.map((item) => <button key={item} className={filter === item ? 'active' : ''} onClick={() => setFilter(item)}>{item}</button>)}
        </div>

        <div className="hotel-grid">
          {visibleHotels.map((hotel, index) => {
            const selected = hotel.id === selectedHotelId;
            const favourite = favourites.includes(hotel.id);
            return (
              <motion.article
                key={hotel.id}
                className={`hotel-card ${selected ? 'selected' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: (index % 3) * 0.08 }}
              >
                <div className="hotel-image-wrap">
                  <ImageWithFallback src={hotel.image} alt={`${hotel.name} dinner setting`} className="hotel-image" loading="lazy" />
                  {hotel.featured && <span className="featured-badge"><Sparkles size={14} /> Featured idea</span>}
                  {selected && <span className="selected-badge"><Check size={15} /> Selected</span>}
                  <button
                    className={`favourite-button ${favourite ? 'active' : ''}`}
                    onClick={() => setFavourites(favourite ? favourites.filter((id) => id !== hotel.id) : [...favourites, hotel.id])}
                    aria-label={favourite ? `Remove ${hotel.name} from favourites` : `Add ${hotel.name} to favourites`}
                  ><Heart size={20} fill={favourite ? 'currentColor' : 'none'} /></button>
                </div>
                <div className="hotel-content">
                  <div className="location-line"><MapPin size={16} /> {hotel.area}</div>
                  <h3>{hotel.name}</h3>
                  <p>{hotel.shortDescription}</p>
                  <div className="tag-row">{hotel.cuisineOptions.map((tag) => <span key={tag}>{tag}</span>)}</div>
                  <div className="ideal-label"><strong>Ideal For</strong><span>{hotel.idealFor}</span></div>
                  <small>{hotel.priceLabel}</small>
                  <div className="card-actions">
                    <button className="secondary-button" onClick={() => setDetails(hotel)}>View Dinner Details</button>
                    <button className="primary-button" onClick={() => selectHotel(hotel)}>{selected ? 'Hotel Selected' : 'Choose This Hotel'}</button>
                  </div>
                </div>
                {selected && <Sticker src="/stickers/mochi-celebrate.svg" alt="Mochi cat celebrating hotel selection" className="hotel-selection-sticker" />}
              </motion.article>
            );
          })}
        </div>
      </div>

      <Modal open={Boolean(details)} onClose={() => setDetails(null)} title={details?.name ?? 'Hotel details'} wide>
        {details && (
          <div className="details-modal-content">
            <div className="details-gallery">
              {details.gallery.map((image, index) => <ImageWithFallback key={image} src={image} alt={`${details.name} view ${index + 1}`} className="details-image" />)}
            </div>
            <span className="eyebrow">Lahore dinner idea</span>
            <h2>{details.name}</h2>
            <div className="location-line"><MapPin size={17} /> {details.area}</div>
            <p>{details.shortDescription}</p>
            <div className="detail-block"><UtensilsCrossed size={20} /><div><strong>Dinner style</strong><p>{details.dinnerStyle}</p></div></div>
            <div className="tag-row">{details.cuisineOptions.map((tag) => <span key={tag}>{tag}</span>)}</div>
            <div className="modal-notice">{birthdayConfig.copy.hotels.availabilityNotice}</div>
            <div className="card-actions">
              <a className="secondary-button" href={details.reservationUrl} target="_blank" rel="noreferrer"><ExternalLink size={18} /> Open map / venue search</a>
              <button className="primary-button" onClick={() => { selectHotel(details); setDetails(null); }}>Choose This Hotel</button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
